import type {
  ContextProvider,
  NetworkAdapter,
  TrackEvent,
  TrackerOptions,
  TrackPlugin,
} from './types';
import type { QueueItem } from './buffer-queue';
import { DEFAULT_TRACK_ENDPOINT } from './constants';
import { DefaultLogger, SafeShell } from './safe-shell';
import { IdentityManager } from './identity';
import { SessionManager } from './session';
import { ContextManager } from './dynamic-context';
import { MaskingMiddleware } from './masking';
import { BufferQueue } from './buffer-queue';
import { Scheduler } from './scheduler';
import { Pipeline } from './pipeline';
import { safeTrackerFactory } from './tracker-factory';

/**
 * 埋点 SDK 核心实例类
 */
export class Tracker {
  private logger: DefaultLogger;
  private safeShell: SafeShell;
  private identityManager: IdentityManager;
  private sessionManager: SessionManager;
  private contextManager: ContextManager;
  private maskingMiddleware: MaskingMiddleware;
  private bufferQueue: BufferQueue;
  private scheduler: Scheduler;
  private pipeline: Pipeline;
  private network?: NetworkAdapter;
  private endpoint: string;
  private destroyed = false;

  constructor(options: TrackerOptions) {
    this.logger = new DefaultLogger();
    this.safeShell = new SafeShell(this.logger);
    const snapshot = this.safeShell.run(() => ({ ...options }), null, 'Tracker.initialize');
    this.destroyed = snapshot === null;
    options = snapshot ?? {
      appId: '',
      storage: { getItem: () => null, setItem: () => undefined, removeItem: () => undefined },
    };
    this.logger.setLevel(options.logLevel ?? 'warn');
    this.identityManager = new IdentityManager(options.storage);
    this.sessionManager = new SessionManager({ sessionTimeoutMs: options.sessionTimeoutMs });
    this.contextManager = new ContextManager();
    this.maskingMiddleware = new MaskingMiddleware();
    this.bufferQueue = new BufferQueue({ maxQueueSize: options.maxQueueSize });
    this.network = options.network;
    this.endpoint = options.endpoint ?? DEFAULT_TRACK_ENDPOINT;
    this.pipeline = new Pipeline(this.safeShell);
    this.scheduler = new Scheduler({
      queue: this.bufferQueue,
      network: options.network,
      endpoint: options.endpoint,
      batchSize: options.batchSize,
      flushIntervalMs: options.flushIntervalMs,
      sendBatchHandler: (items) => this.sendQueuedItems(items),
    });

    this.initDefaultPlugins();
    this.safeShell.run(() => {
      if (!this.destroyed) this.scheduler.start();
    }, undefined, 'Tracker.start');
  }

  private initDefaultPlugins(): void {
    // 默认内置数据脱敏插件
    this.use({
      name: 'hss-masking-plugin',
      transform: (event) => this.maskEvent(event),
    });
  }

  public use(plugin: TrackPlugin): this {
    this.safeShell.run(() => {
      if (this.destroyed) return;
      const initialized = plugin.init?.(this);
      if (initialized && typeof initialized.then === 'function') {
        return this.safeShell.runAsync(async () => {
          await initialized;
          if (!this.destroyed) this.pipeline.use(plugin);
        }, undefined, 'Tracker.use.init');
      }
      this.pipeline.use(plugin);
    }, undefined, 'Tracker.use');
    return this;
  }

  public setContext(ctx: Record<string, any>): this {
    this.safeShell.run(() => this.contextManager.setStaticContext(ctx), undefined, 'Tracker.setContext');
    return this;
  }

  public registerContextProvider(name: string, provider: ContextProvider): this {
    this.safeShell.run(() => this.contextManager.registerProvider(name, provider), undefined, 'Tracker.registerContextProvider');
    return this;
  }

  /** @internal 仅供包内协议插件读取最终上报地址。 */
  private getEndpoint(): string {
    return this.endpoint;
  }

  public track(eventName: string, properties: Record<string, any> = {}): void {
    this.safeShell.run(() => {
      if (this.destroyed) return;
      if (!this.pipeline.runBeforeProduce(eventName, properties)) return;

      const identity = this.identityManager.getIdentity();
      const dynamicContext = this.contextManager.resolveContext();

      const event: TrackEvent = {
        eventName,
        timestamp: Date.now(),
        uuid: identity.uuid,
        sessionId: this.sessionManager.getSessionId(),
        isNewVisit: identity.isNewVisit,
        properties: { ...dynamicContext, ...properties },
      };

      const transformed = this.pipeline.runTransform(event);
      if (!transformed) return;

      if (!this.pipeline.runBeforeSend(transformed)) return;
      const safeEvent = this.maskEvent(transformed);
      if (!safeEvent) return;

      this.bufferQueue.enqueue({
        id: `${safeEvent.eventName}_${safeEvent.timestamp}`,
        data: safeEvent,
      });

    }, undefined, 'Tracker.track');
  }

  private maskEvent(event: TrackEvent): TrackEvent | null {
    return this.safeShell.run(() => {
      return { ...event, properties: this.maskingMiddleware.mask(event.properties) };
    }, null, 'Tracker.mask');
  }

  /**
   * 协议插件使用的统一网络出口。
   */
  private async request(
    url: string,
    data: string | Record<string, unknown>,
    headers?: Record<string, string>
  ): Promise<{ status: number; data: any }> {
    if (!this.network) {
      return { status: 0, data: { code: '0', message: 'No network adapter' } };
    }
    return this.network.post(url, data, headers);
  }

  private async sendQueuedItems(items: QueueItem[]): Promise<boolean | number> {
    if (this.pipeline.hasSendHandler()) {
      let sentCount = 0;
      for (const item of items) {
        const event = item.data as TrackEvent;
        const success = await this.pipeline.runSend(event);
        this.pipeline.runAfterSend(event, success);
        if (!success) return sentCount;
        sentCount += 1;
      }
      return sentCount;
    }

    const response = await this.request(
      this.endpoint,
      { events: items.map((item) => item.data) }
    );
    const code = response.data?.code;
    const success = response.status === 200 && (code === '10000' || code === 10000);
    for (const item of items) {
      this.pipeline.runAfterSend(item.data as TrackEvent, success);
    }
    return success;
  }

  public async flush(): Promise<void> {
    await this.safeShell.runAsync(async () => {
      await this.scheduler.flush();
    }, undefined, 'Tracker.flush');
  }

  public destroy(): void {
    this.destroyed = true;
    this.safeShell.run(() => this.scheduler.stop(), undefined, 'Tracker.destroy.scheduler');
    this.safeShell.run(() => this.pipeline.destroy(), undefined, 'Tracker.destroy.plugins');
  }
}

/**
 * 创建 SDK 实例工厂函数
 */
export function createTracker(options: TrackerOptions): Tracker {
  return safeTrackerFactory(() => new Tracker({ ...options }));
}
